/**
 * Logger Service
 * Centralized logging with module namespacing and log levels
 */

import { LogLevel, LogEntry } from '../types';

class Logger {
  private static instance: Logger;
  private level: LogLevel = LogLevel.INFO;
  private initialized = false;

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  /**
   * Initialize logger with level from URL params or default
   */
  initialize(): void {
    if (this.initialized) return;

    // Check URL params for log level override
    const urlLevel = this.getUrlLogLevel();
    if (urlLevel !== null) {
      this.level = urlLevel;
    }

    this.initialized = true;
    this.info('Logger', 'Logger initialized', { level: LogLevel[this.level] });
  }

  /**
   * Get log level from URL parameter
   */
  private getUrlLogLevel(): LogLevel | null {
    const params = new URLSearchParams(window.location.search);
    const logParam = params.get('log');

    switch (logParam?.toLowerCase()) {
      case 'off': return LogLevel.OFF;
      case 'error': return LogLevel.ERROR;
      case 'warn': return LogLevel.WARN;
      case 'info': return LogLevel.INFO;
      case 'debug': return LogLevel.DEBUG;
      case 'trace': return LogLevel.TRACE;
      default: return null;
    }
  }

  /**
   * Set log level programmatically
   */
  setLevel(level: LogLevel): void {
    this.level = level;
  }

  /**
   * Get current log level
   */
  getLevel(): LogLevel {
    return this.level;
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, module: string, message: string, data?: unknown): void {
    if (level > this.level) return;

    const entry: LogEntry = {
      level,
      module,
      message,
      data,
      timestamp: new Date()
    };

    const formatted = this.format(entry);

    switch (level) {
      case LogLevel.ERROR:
        console.error(formatted, data !== undefined ? data : '');
        break;
      case LogLevel.WARN:
        console.warn(formatted, data !== undefined ? data : '');
        break;
      case LogLevel.INFO:
        console.log(formatted, data !== undefined ? data : '');
        break;
      case LogLevel.DEBUG:
        console.debug(formatted, data !== undefined ? data : '');
        break;
      case LogLevel.TRACE:
        console.trace(formatted, data !== undefined ? data : '');
        break;
    }
  }

  /**
   * Format log entry
   */
  private format(entry: LogEntry): string {
    const time = entry.timestamp.toISOString().substring(11, 23);
    const levelName = LogLevel[entry.level].padEnd(5);
    return `[${entry.module}] ${time} ${levelName} ${entry.message}`;
  }

  // Public logging methods
  error(module: string, message: string, data?: unknown): void {
    this.log(LogLevel.ERROR, module, message, data);
  }

  warn(module: string, message: string, data?: unknown): void {
    this.log(LogLevel.WARN, module, message, data);
  }

  info(module: string, message: string, data?: unknown): void {
    this.log(LogLevel.INFO, module, message, data);
  }

  debug(module: string, message: string, data?: unknown): void {
    this.log(LogLevel.DEBUG, module, message, data);
  }

  trace(module: string, message: string, data?: unknown): void {
    this.log(LogLevel.TRACE, module, message, data);
  }
}

/**
 * Module-specific logger wrapper
 */
export class ModuleLogger {
  private moduleName: string;
  private logger: Logger;

  constructor(moduleName: string) {
    this.moduleName = moduleName;
    this.logger = Logger.getInstance();
  }

  error(message: string, data?: unknown): void {
    this.logger.error(this.moduleName, message, data);
  }

  warn(message: string, data?: unknown): void {
    this.logger.warn(this.moduleName, message, data);
  }

  info(message: string, data?: unknown): void {
    this.logger.info(this.moduleName, message, data);
  }

  debug(message: string, data?: unknown): void {
    this.logger.debug(this.moduleName, message, data);
  }

  trace(message: string, data?: unknown): void {
    this.logger.trace(this.moduleName, message, data);
  }

  /**
   * Timing helper - returns function to call when done
   */
  time(label: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.debug(`${label}: ${duration.toFixed(2)}ms`);
    };
  }
}

/**
 * Create a logger for a specific module
 */
export function createLogger(moduleName: string): ModuleLogger {
  return new ModuleLogger(moduleName);
}

/**
 * Initialize the global logger
 */
export function initializeLogger(): void {
  Logger.getInstance().initialize();
}

/**
 * Set global log level
 */
export function setLogLevel(level: LogLevel): void {
  Logger.getInstance().setLevel(level);
}

export { LogLevel };
